import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Fab, Container, Grid, IconButton, Chip, Link, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { WhatsApp, Phone, Email, LocationOn, Schedule, CheckCircle } from '@mui/icons-material';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const Layout = ({ children }) => {
  return (
    <>
      <AppBar position="fixed" sx={{ ...glassStyle, background: 'rgba(27, 94, 32, 0.95)' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <img src="/logo.svg" alt="Green Mountain Landscaping Logo" style={{ width: 40, height: 40, marginRight: 8 }} />
            <Typography variant="h6" component={RouterLink} to="/" sx={{ fontWeight: 600, textDecoration: 'none', color: 'inherit', display: { xs: 'none', sm: 'block' } }}>
              Green Mountain Landscaping
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" component={RouterLink} to="/get-quote" sx={{ fontWeight: 600 }}>Get Quote</Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 8 }}>
        {children}
      </Box>

      {/* Footer */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
        color: 'white', 
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <img src="/logo.svg" alt="Logo" style={{ width: 40, height: 40, marginRight: 12 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Green Mountain
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, lineHeight: 1.6 }}>
                Professional landscape design & maintenance services in West Vancouver, BC
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<CheckCircle sx={{ fontSize: 16 }} />} 
                  label="Licensed & Insured" 
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
                <Chip 
                  label="Free Quotes" 
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Contact Info</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                  <Link href="tel:+16042020053" color="inherit" underline="hover">
                    (604) 202-0053
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                  <Link href="mailto:lolooit@gmail.com" color="inherit" underline="hover">
                    lolooit@gmail.com
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)', mt: 0.2 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    1504-1552 Esquimalt Ave<br />West Vancouver, BC V7V 1R3
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Mon-Fri 8AM-6PM, Sat 9AM-5PM
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Connect With Us</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <IconButton 
                  href="tel:+16042020053" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Phone />
                </IconButton>
                <IconButton 
                  href="mailto:lolooit@gmail.com" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Email />
                </IconButton>
                <IconButton 
                  href="https://wa.me/16042020053" 
                  target="_blank"
                  sx={{ 
                    bgcolor: '#25d366', 
                    color: 'white',
                    '&:hover': { bgcolor: '#128c7e', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <WhatsApp />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Service Areas:
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.5 }}>
                West Vancouver • North Vancouver • Burnaby • Richmond • Vancouver & surrounding Metro Vancouver areas
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 4 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © 2024 Green Mountain Landscaping. All rights reserved. | Professional Landscaping Services in Metro Vancouver
            </Typography>
          </Box>
        </Container>
      </Box>

      <Fab
        color="success"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: '#25d366',
          '&:hover': { bgcolor: '#128c7e' }
        }}
        href="https://wa.me/16042020053"
        target="_blank"
      >
        <WhatsApp />
      </Fab>
    </>
  );
};

export default Layout;